import React, { useState, useEffect } from 'react';
import { 
  Database, HardDrive, Trash2, RefreshCw, Activity, BarChart3,
  Download, AlertTriangle, CheckCircle, XCircle, Cpu, Clock,
  Users, Wifi, CreditCard, Play, Upload
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;


const fmtDT = (v) => (v ? new Date(v).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) : '-');
const FREQ_LABEL = { daily: 'Harian', weekly: 'Mingguan (Senin)', monthly: 'Bulanan (tgl 1)', manual: 'Manual' };

const BackupSchedulesCard = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v2/superadmin/backup-schedules`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data?.data || []))
      .catch(() => setRows([]));
  }, []);
  return (
    <Card data-testid="card-backup-schedules">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Jadwal Backup Otomatis
        </CardTitle>
        <p className="text-sm text-slate-600 dark:text-slate-300">Backup per tenant. Jadwal diatur di menu Backup Manager masing-masing tenant.</p>
      </CardHeader>
      <CardContent>
        {rows === null ? (
          <p className="text-sm text-slate-500">Memuat...</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="backup-schedules-empty">Belum ada tenant dengan jadwal backup.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="backup-schedules-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200 dark:border-slate-700">
                  <th className="py-2 pr-4">Tenant</th><th className="py-2 pr-4">Jadwal</th><th className="py-2 pr-4">Terakhir</th>
                  <th className="py-2 pr-4">Berikutnya</th><th className="py-2 pr-4">File Terbaru</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.tenant_id} className="border-b border-slate-100 dark:border-slate-800" data-testid={`backup-schedule-row-${r.tenant_id}`}>
                    <td className="py-2 pr-4 font-medium">{r.tenant_name}</td>
                    <td className="py-2 pr-4">{r.enabled ? `${FREQ_LABEL[r.frequency] || r.frequency} ${r.time} · simpan ${r.retention_days} hari` : 'Nonaktif'}</td>
                    <td className="py-2 pr-4">{fmtDT(r.last_run_at)}</td>
                    <td className="py-2 pr-4">{fmtDT(r.next_run_at)}</td>
                    <td className="py-2 pr-4">{r.latest_file ? `${r.latest_file.size_mb} MB · ${r.file_count} file` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SuperAdminTools = () => {
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [scheduleSettings, setScheduleSettings] = useState({
    enabled: false,
    frequency: 'daily',
    time: '02:00',
    day_of_week: 1,
    day_of_month: 1,
    retention_days: 30
  });
  
  // Progress Dialog State
  const [progressDialog, setProgressDialog] = useState({
    open: false,
    title: '',
    message: '',
    progress: 0,
    status: 'running', // running, success, error
    details: []
  });

  // Delete Logs Dialog State
  const [deleteLogsDialog, setDeleteLogsDialog] = useState({
    open: false,
    days: 30
  });

  // Preview Logs State
  const [previewLogsDialog, setPreviewLogsDialog] = useState({
    open: false,
    files: [],
    selectedFiles: [],
    totalSize: 0,
    days: 30
  });

  // ACS Settings State
  // 2026-07-30: Data Health Check — RADIUS sync
  // ===== Kesehatan Disk VPS state =====
  const [diskHealth, setDiskHealth] = useState(null);
  const [diskLoading, setDiskLoading] = useState(false);
  const [diskCleaning, setDiskCleaning] = useState(false);

  const fmtBytes = (b) => {
    if (b === null || b === undefined) return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = Number(b); let i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return v.toFixed(v >= 100 ? 0 : 1) + ' ' + units[i];
  };

  const loadDiskHealth = async () => {
    setDiskLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(API_URL + '/api/v2/system/disk-health', { headers: { Authorization: 'Bearer ' + token } });
      setDiskHealth(res.data?.data || null);
    } catch (e) {
      toast.error('Gagal memuat kesehatan disk: ' + (e?.response?.data?.detail || e.message));
    } finally { setDiskLoading(false); }
  };

  const runDiskCleanup = async () => {
    if (!window.confirm('Jalankan cleanup log sekarang? (purge binlog, kompres log RADIUS/Nginx, vacuum journald)')) return;
    setDiskCleaning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(API_URL + '/api/v2/system/disk-cleanup', {}, { headers: { Authorization: 'Bearer ' + token }, timeout: 300000 });
      const freed = res.data?.data?.freed_bytes || 0;
      toast.success('Cleanup selesai. Ruang dibebaskan: ' + fmtBytes(freed));
      if (res.data?.data?.binlog) toast.info(res.data.data.binlog, { duration: 8000 });
      loadDiskHealth();
    } catch (e) {
      toast.error('Cleanup gagal: ' + (e?.response?.data?.detail || e.message));
    } finally { setDiskCleaning(false); }
  };

  useEffect(() => { loadDiskHealth(); }, []);

  const [radiusSyncReport, setRadiusSyncReport] = useState(null);
  const [radiusSyncLoading, setRadiusSyncLoading] = useState(false);

  const [acsSettings, setAcsSettings] = useState({
    periodic_inform_interval: 30,
    wan_sync_loading: false,
    last_wan_sync: null,
    wan_sync_result: null,
    loading: false
  });

  useEffect(() => {
    fetchBackups();
    fetchHealthCheck();
    fetchDbStats();
    fetchAcsSettings();
  }, []);


  // ACS Settings Functions
  const fetchAcsSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/v2/superadmin/acs-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAcsSettings(prev => ({
          ...prev,
          periodic_inform_interval: response.data.data.periodic_inform_interval
        }));
      }
    } catch (error) {
      console.error("Error fetching ACS settings:", error);
    }
  };

  const updateAcsSettings = async () => {
    setAcsSettings(prev => ({ ...prev, loading: true }));
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/api/v2/superadmin/acs-settings`,
        { periodic_inform_interval: acsSettings.periodic_inform_interval },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error("Gagal update: " + response.data.error);
      }
    } catch (error) {
      toast.error("Error: " + (error.response?.data?.detail || error.message));
    } finally {
      setAcsSettings(prev => ({ ...prev, loading: false }));
    }
  };

  // WAN Device Sync Functions
  const syncWanDevices = async () => {
    setAcsSettings(prev => ({ ...prev, wan_sync_loading: true }));
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/v2/superadmin/sync-wan-devices`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setAcsSettings(prev => ({
          ...prev,
          wan_sync_result: response.data,
          last_wan_sync: new Date().toISOString()
        }));
      } else {
        toast.error("Gagal sync: " + response.data.error);
      }
    } catch (error) {
      toast.error("Error: " + (error.response?.data?.detail || error.message));
    } finally {
      setAcsSettings(prev => ({ ...prev, wan_sync_loading: false }));
    }
  };

  const fetchMigrationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/superadmin/migration/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setMigrationStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching migration status:', error);
    }
  };

  const handleUpdatePaymentStatus = async () => {
    if (!window.confirm('Apakah Anda yakin ingin mengupdate Status Pembayaran semua customer?\n\nIni akan menghitung ulang status LUNAS/BELUM LUNAS berdasarkan invoice.')) {
      return;
    }
    
    setMigrationLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/superadmin/migration/update-payment-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Berhasil! ${response.data.data.total_customers} customer diupdate. LUNAS: ${response.data.data.lunas}, BELUM LUNAS: ${response.data.data.belum_lunas}`);
        fetchMigrationStatus();
      }
    } catch (error) {
      toast.error('Gagal mengupdate: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleSyncOnlineStatus = async () => {
    if (!window.confirm('Apakah Anda yakin ingin sync Status Online semua customer?\n\nCustomer tanpa data is_online akan diset ke OFFLINE.')) {
      return;
    }
    
    setMigrationLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/superadmin/migration/sync-online-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Berhasil! Online: ${response.data.data.online}, Offline: ${response.data.data.offline}`);
        fetchMigrationStatus();
      }
    } catch (error) {
      toast.error('Gagal sync: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMigrationLoading(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/superadmin/backup/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBackups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Failed to fetch backups');
    }
  };

  const fetchHealthCheck = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/superadmin/maintenance/health-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setHealthData(response.data.health);
      }
    } catch (error) {
      console.error('Error fetching health:', error);
    }
  };

  const fetchDbStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/superadmin/maintenance/db-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDbStats(response.data.database_stats);
      }
    } catch (error) {
      console.error('Error fetching db stats:', error);
    }
  };

  const fetchScheduleSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/superadmin/backup/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setScheduleSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  };

  const handleUpdateSchedule = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/superadmin/backup/schedule`,
        scheduleSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Backup schedule updated successfully!');
        fetchScheduleSettings();
      }
    } catch (error) {
      toast.error('Failed to update schedule: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 2026-07-29: Backup file actions (Download / Restore / Delete)
  const handleDownloadBackup = async (backup) => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(
        `${API_URL}/api/v2/tools/download-backup/${encodeURIComponent(backup.name)}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup ${backup.name} di-download`);
    } catch (e) {
      console.error('Download backup error:', e);
      toast.error(`Gagal download backup: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleRestoreBackup = async (backup) => {
    const isFullDb = backup.name.startsWith('mysql_backup_') && backup.name.endsWith('.sql.gz');
    const scopeText = isFullDb
      ? '⚠️ FULL DATABASE (semua tenant + data global). Data saat ini AKAN DITIMPA total.'
      : '⚠️ Data 1 TENANT (mengganti data tenant terkait pada backup).';
    const confirmMsg = `RESTORE BACKUP\n\nFile: ${backup.name}\nScope: ${scopeText}\n\nApakah Anda yakin ingin melanjutkan? Aksi ini TIDAK BISA DI-UNDO. Ketik "RESTORE" untuk konfirmasi.`;
    const answer = window.prompt(confirmMsg);
    if (answer !== 'RESTORE') {
      toast.info('Restore dibatalkan');
      return;
    }
    setProgressDialog({
      open: true,
      title: `♻️ Restoring ${isFullDb ? 'Full DB' : 'Tenant'} Backup`,
      message: `Restoring dari ${backup.name}...`,
      progress: 20,
      status: 'running',
      details: [`Loading ${backup.name}...`],
    });
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/v2/tools/restore-backup/${encodeURIComponent(backup.name)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
      );
      setProgressDialog(prev => ({ ...prev, progress: 100, status: 'success', message: 'Restore berhasil', details: [...(prev.details || []), '✓ Restore complete'] }));
      toast.success(resp.data?.message || 'Restore backup berhasil');
      setTimeout(() => setProgressDialog(prev => ({ ...prev, open: false })), 2000);
      // Refresh backup list
      fetchBackups();
    } catch (e) {
      console.error('Restore backup error:', e);
      const detail = e?.response?.data?.detail || e.message;
      setProgressDialog(prev => ({ ...prev, status: 'error', message: `Gagal: ${detail}`, details: [...(prev.details || []), `✗ ${detail}`] }));
      toast.error(`Restore gagal: ${detail}`);
    }
  };

  const handleDeleteBackup = async (backup) => {
    if (!window.confirm(`Hapus backup "${backup.name}"?\n\nAksi ini tidak dapat dibatalkan.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/v2/tools/delete-backup/${encodeURIComponent(backup.name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Backup ${backup.name} dihapus`);
      fetchBackups();
    } catch (e) {
      console.error('Delete backup error:', e);
      toast.error(`Gagal hapus: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleCreateFullBackup = async () => {
    setProgressDialog({
      open: true,
      title: '💾 Creating Full Backup',
      message: 'Backing up all database collections...',
      progress: 0,
      status: 'running',
      details: ['Initializing backup process...']
    });

    try {
      const token = localStorage.getItem('token');
      
      // Simulate progress with realistic steps
      const collections = [
        'users', 'customers', 'invoices', 'payments', 'branches',
        'service_plans', 'expenses', 'tenants', 'mikrotik_configs'
      ];
      
      const progressStep = 80 / collections.length;
      let currentProgress = 0;

      const progressInterval = setInterval(() => {
        if (currentProgress < 80) {
          const collectionIndex = Math.floor(currentProgress / progressStep);
          currentProgress += progressStep;
          
          setProgressDialog(prev => ({
            ...prev,
            progress: Math.min(currentProgress, 80),
            details: [
              ...prev.details,
              `Backing up ${collections[collectionIndex]}...`
            ].slice(-6)
          }));
        }
      }, 400);

      const response = await axios.post(
        `${API_URL}/api/superadmin/backup/full`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      clearInterval(progressInterval);

      if (response.data.success) {
        setProgressDialog({
          open: true,
          title: '✅ Backup Complete',
          message: 'Full database backup created successfully!',
          progress: 100,
          status: 'success',
          details: [
            'All collections backed up',
            `File: ${response.data.filename}`,
            'Backup compressed and saved'
          ]
        });

        setTimeout(() => {
          setProgressDialog(prev => ({ ...prev, open: false }));
          fetchBackups();
        }, 3000);
      }
    } catch (error) {
      setProgressDialog({
        open: true,
        title: '❌ Backup Failed',
        message: error.response?.data?.detail || error.message,
        progress: 0,
        status: 'error',
        details: ['Failed to create backup']
      });

      setTimeout(() => {
        setProgressDialog(prev => ({ ...prev, open: false }));
      }, 3000);
    }
  };

  const handleOptimizeDatabase = async () => {
    // Show progress dialog
    setProgressDialog({
      open: true,
      title: '🔧 Optimizing Database',
      message: 'Rebuilding indexes and optimizing collections...',
      progress: 0,
      status: 'running',
      details: ['Initializing optimization process...']
    });

    try {
      const token = localStorage.getItem('token');
      
      // Simulate progress
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        if (progress <= 90) {
          setProgressDialog(prev => ({
            ...prev,
            progress,
            details: [
              ...prev.details,
              `Optimizing collections... ${progress}%`
            ].slice(-5) // Keep last 5 messages
          }));
        }
      }, 300);

      const response = await axios.post(
        `${API_URL}/api/v2/superadmin/maintenance/optimize-db`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      clearInterval(progressInterval);

      if (response.data.success) {
        setProgressDialog({
          open: true,
          title: '✅ Database Optimized',
          message: 'All collections have been successfully optimized!',
          progress: 100,
          status: 'success',
          details: Object.entries(response.data.results || {}).map(([name, status]) => 
            `${name}: ${status}`
          )
        });

        setTimeout(() => {
          setProgressDialog(prev => ({ ...prev, open: false }));
          fetchDbStats(); // Refresh stats
        }, 3000);
      }
    } catch (error) {
      setProgressDialog({
        open: true,
        title: '❌ Optimization Failed',
        message: error.response?.data?.detail || error.message,
        progress: 0,
        status: 'error',
        details: ['Failed to optimize database']
      });

      setTimeout(() => {
        setProgressDialog(prev => ({ ...prev, open: false }));
      }, 3000);
    }
  };

  const handleClearCache = async () => {
    setProgressDialog({
      open: true,
      title: '🗑️ Clearing Cache',
      message: 'Removing cached files...',
      progress: 0,
      status: 'running',
      details: ['Scanning cache directories...']
    });

    try {
      const token = localStorage.getItem('token');
      
      // Simulate progress
      const steps = [
        { progress: 20, message: 'Clearing Python cache...' },
        { progress: 40, message: 'Removing __pycache__ directories...' },
        { progress: 60, message: 'Clearing pytest cache...' },
        { progress: 80, message: 'Finalizing cleanup...' }
      ];

      for (const step of steps) {
        await new Promise(resolve => setTimeout(resolve, 400));
        setProgressDialog(prev => ({
          ...prev,
          progress: step.progress,
          details: [...prev.details, step.message].slice(-5)
        }));
      }

      const response = await axios.post(
        `${API_URL}/api/superadmin/maintenance/clear-cache`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setProgressDialog({
          open: true,
          title: '✅ Cache Cleared',
          message: 'All cache files removed successfully!',
          progress: 100,
          status: 'success',
          details: response.data.cleared || ['Cache cleared']
        });

        setTimeout(() => {
          setProgressDialog(prev => ({ ...prev, open: false }));
        }, 3000);
      }
    } catch (error) {
      setProgressDialog({
        open: true,
        title: '❌ Clear Cache Failed',
        message: error.response?.data?.detail || error.message,
        progress: 0,
        status: 'error',
        details: ['Failed to clear cache']
      });

      setTimeout(() => {
        setProgressDialog(prev => ({ ...prev, open: false }));
      }, 3000);
    }
  };

  const handleDeleteOldLogs = () => {
    // Open dialog to get days input
    setDeleteLogsDialog({
      open: true,
      days: 30
    });
  };

  const confirmDeleteLogs = async () => {
    const days = deleteLogsDialog.days;
    
    // Close input dialog
    setDeleteLogsDialog({ open: false, days: 30 });

    // Show loading
    setProgressDialog({
      open: true,
      title: '🔍 Scanning Logs',
      message: `Finding logs older than ${days} days...`,
      progress: 50,
      status: 'running',
      details: ['Scanning log directory...']
    });

    try {
      const token = localStorage.getItem('token');
      
      // Fetch preview
      const response = await axios.get(
        `${API_URL}/api/superadmin/maintenance/preview-logs?days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Close progress
      setProgressDialog(prev => ({ ...prev, open: false }));

      if (response.data.success) {
        const files = response.data.files;
        
        if (files.length === 0) {
          toast.info(`No log files older than ${days} days found`);
          return;
        }

        // Show preview dialog with all files selected by default
        setPreviewLogsDialog({
          open: true,
          files: files,
          selectedFiles: files.map(f => f.path), // All selected by default
          totalSize: response.data.total_size_mb,
          days: days
        });
      }
    } catch (error) {
      setProgressDialog({
        open: true,
        title: '❌ Scan Failed',
        message: error.response?.data?.detail || error.message,
        progress: 0,
        status: 'error',
        details: ['Failed to scan logs']
      });

      setTimeout(() => {
        setProgressDialog(prev => ({ ...prev, open: false }));
      }, 3000);
    }
  };

  const executeDeletion = async () => {
    const selectedPaths = previewLogsDialog.selectedFiles;
    
    if (selectedPaths.length === 0) {
      toast.error('No files selected');
      return;
    }

    // Close preview
    setPreviewLogsDialog({ open: false, files: [], selectedFiles: [], totalSize: 0, days: 30 });

    // Show progress
    setProgressDialog({
      open: true,
      title: '🗂️ Deleting Logs',
      message: `Deleting ${selectedPaths.length} log file(s)...`,
      progress: 0,
      status: 'running',
      details: ['Starting deletion...']
    });

    try {
      const token = localStorage.getItem('token');
      
      // Simulate progress
      const progressStep = 80 / selectedPaths.length;
      let currentProgress = 0;

      for (let i = 0; i < selectedPaths.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        currentProgress += progressStep;
        setProgressDialog(prev => ({
          ...prev,
          progress: Math.min(currentProgress, 80),
          details: [
            ...prev.details,
            `Deleting file ${i + 1}/${selectedPaths.length}...`
          ].slice(-5)
        }));
      }

      const response = await axios.post(
        `${API_URL}/api/v2/superadmin/maintenance/delete-logs`,
        { days_old: 30 },
        { headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } }
      );

      if (response.data.success) {
        const deletedCount = response.data.deleted_files?.length || 0;
        setProgressDialog({
          open: true,
          title: '✅ Logs Deleted',
          message: `Successfully deleted ${deletedCount} old log file(s)`,
          progress: 100,
          status: 'success',
          details: response.data.deleted_files || ['No old logs found']
        });

        setTimeout(() => {
          setProgressDialog(prev => ({ ...prev, open: false }));
        }, 4000);
      }
    } catch (error) {
      setProgressDialog({
        open: true,
        title: '❌ Delete Failed',
        message: error.response?.data?.detail || error.message,
        progress: 0,
        status: 'error',
        details: ['Failed to delete logs']
      });

      setTimeout(() => {
        setProgressDialog(prev => ({ ...prev, open: false }));
      }, 3000);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString('id-ID');
  };

  // 2026-07-30: Data Health Check handlers
  const runRadiusSyncCheck = async (dryRun) => {
    setRadiusSyncLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        API_URL + '/api/v2/tools/radius-sync-check',
        { dry_run: !!dryRun },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setRadiusSyncReport(res.data);
      if (dryRun) {
        const mc = res.data.mismatch_count || {};
        toast.info('Ditemukan ' + (mc.isolir_missing || 0) + ' isolir palsu + ' + (mc.unisolir_stale || 0) + ' normal ter-block palsu');
      } else {
        const f = res.data.fixed || {};
        const total = (f.inserted_pool_isolir || 0) + (f.deleted_pool_isolir || 0);
        toast.success('Auto-fix selesai. Total ' + total + ' record diperbaiki dalam ' + res.data.duration_ms + 'ms.');
      }
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || 'Gagal menjalankan sync check');
    } finally {
      setRadiusSyncLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">🔧 Super Admin Tools</h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1" data-testid="superadmin-tools-subtitle">
          Database Management & System Maintenance
        </p>
      </div>

      {/* Kesehatan Disk VPS */}
      <Card className="border-2 border-indigo-200 dark:border-indigo-800" data-testid="card-disk-health">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-indigo-600" />
            Kesehatan Disk VPS
            {diskHealth && (
              <Badge variant={diskHealth.used_pct >= 85 ? 'destructive' : diskHealth.used_pct >= 70 ? 'secondary' : 'default'} data-testid="disk-usage-badge">
                {diskHealth.used_pct}% terpakai
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {diskLoading && !diskHealth ? (
            <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="disk-loading"><RefreshCw className="w-4 h-4 animate-spin" /> Memuat status disk...</div>
          ) : diskHealth ? (
            <>
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span data-testid="disk-used-label">Terpakai {fmtBytes(diskHealth.used_bytes)} dari {fmtBytes(diskHealth.total_bytes)}</span>
                  <span data-testid="disk-avail-label">Sisa {fmtBytes(diskHealth.avail_bytes)}</span>
                </div>
                <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden" data-testid="disk-usage-bar">
                  <div
                    className={'h-full rounded-full transition-all ' + (diskHealth.used_pct >= 85 ? 'bg-rose-500' : diskHealth.used_pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500')}
                    style={{ width: diskHealth.used_pct + '%' }}
                  />
                </div>
                <div className="text-xs text-slate-500 mt-1" data-testid="disk-binlog-count">
                  Binlog MariaDB aktif: {diskHealth.binlog_count} file
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="disk-breakdown">
                {[
                  ['Log MySQL/Binlog', diskHealth.breakdown?.mysql_log],
                  ['Log FreeRADIUS', diskHealth.breakdown?.freeradius_log],
                  ['Log Nginx', diskHealth.breakdown?.nginx_log],
                  ['Journald', diskHealth.breakdown?.journald],
                  ['Log MongoDB', diskHealth.breakdown?.mongodb_log],
                  ['Folder /var/www', diskHealth.breakdown?.www],
                  ['Backup /var/backups', diskHealth.breakdown?.backups],
                  ['Folder /home', diskHealth.breakdown?.home],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{fmtBytes(val)}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={runDiskCleanup} disabled={diskCleaning} data-testid="disk-cleanup-btn">
                  {diskCleaning ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                  {diskCleaning ? 'Membersihkan...' : 'Bersihkan Log Sekarang'}
                </Button>
                <Button size="sm" variant="outline" onClick={loadDiskHealth} disabled={diskLoading} data-testid="disk-refresh-btn">
                  <RefreshCw className={'w-4 h-4 mr-1.5 ' + (diskLoading ? 'animate-spin' : '')} /> Refresh
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Cleanup aman: purge binlog lama, kompres log RADIUS/Nginx lama (otomatis hapus setelah 14 hari), vacuum journald ke 500MB. Data akuntansi tetap tersimpan di database.
              </p>
            </>
          ) : (
            <div className="text-sm text-slate-500" data-testid="disk-empty">Data disk belum tersedia.</div>
          )}
        </CardContent>
      </Card>

      {/* 2026-07-30: Data Health Check — RADIUS ↔ DB Sync */}
      <Card className="border-2 border-amber-200 dark:border-amber-800">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            🩺 Data Health Check — RADIUS ↔ DB Sync
          </CardTitle>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Deteksi & perbaiki inkonsistensi antara flag <code>is_isolated</code> di DB dengan attribute <code>Framed-Pool</code> / <code>Mikrotik-Address-List</code> di RADIUS.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => runRadiusSyncCheck(true)}
              disabled={radiusSyncLoading}
              className="h-9 px-4 rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
              data-testid="btn-radius-sync-scan"
            >
              {radiusSyncLoading ? 'Sedang scan...' : '🔍 Scan (Dry-Run)'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!window.confirm('Auto-fix akan MENGUBAH radreply. Yakin lanjut?')) return;
                runRadiusSyncCheck(false);
              }}
              disabled={radiusSyncLoading}
              className="h-9 px-4 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium disabled:opacity-50"
              data-testid="btn-radius-sync-fix"
            >
              {radiusSyncLoading ? 'Fixing...' : '🛠️ Auto-Fix Semua'}
            </button>
          </div>
          {radiusSyncReport && (
            <div className="mt-2 rounded-md border border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-900 text-sm">
              <div className="flex flex-wrap gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Isolir Palsu (Group C)</div>
                  <div className="text-2xl font-bold text-red-600">{(radiusSyncReport.mismatch_count && radiusSyncReport.mismatch_count.isolir_missing) || 0}</div>
                  <div className="text-xs text-slate-500">DB bilang isolir, RADIUS masih normal</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Ter-Block Palsu (Group D)</div>
                  <div className="text-2xl font-bold text-orange-600">{(radiusSyncReport.mismatch_count && radiusSyncReport.mismatch_count.unisolir_stale) || 0}</div>
                  <div className="text-xs text-slate-500">DB bilang normal, RADIUS masih pool-isolir</div>
                </div>
                {!radiusSyncReport.dry_run && radiusSyncReport.fixed && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Fixed</div>
                    <div className="text-2xl font-bold text-emerald-600">
                      {(radiusSyncReport.fixed.inserted_pool_isolir || 0) + (radiusSyncReport.fixed.deleted_pool_isolir || 0)}
                    </div>
                    <div className="text-xs text-slate-500">
                      +{radiusSyncReport.fixed.inserted_pool_isolir || 0} isolir · -{radiusSyncReport.fixed.deleted_pool_isolir || 0} unisolir
                    </div>
                  </div>
                )}
                {radiusSyncReport.skipped_special_pool > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Skipped</div>
                    <div className="text-lg font-bold text-slate-600">{radiusSyncReport.skipped_special_pool}</div>
                    <div className="text-xs text-slate-500">Paket khusus (Paket_Lite dll)</div>
                  </div>
                )}
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Terakhir dijalankan: {radiusSyncReport.finished_at || radiusSyncReport.started_at} ·
                Mode: <b>{radiusSyncReport.dry_run ? 'DRY-RUN' : 'FIX'}</b>
                {radiusSyncReport.duration_ms ? ' · Durasi: ' + radiusSyncReport.duration_ms + 'ms' : ''}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Health Overview */}
      {healthData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Database</p>
                  <p className="text-2xl font-bold">
                    {healthData.database === 'healthy' ? (
                      <CheckCircle className="w-6 h-6 text-green-500 inline" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-500 inline" />
                    )}
                  </p>
                </div>
                <Database className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Disk Usage</p>
                  <p className="text-2xl font-bold">{healthData.disk.percent}%</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {healthData.disk.free_gb}GB free
                  </p>
                </div>
                <HardDrive className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Memory</p>
                  <p className="text-2xl font-bold">{healthData.memory.percent}%</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {healthData.memory.available_gb}GB available
                  </p>
                </div>
                <Activity className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">CPU</p>
                  <p className="text-2xl font-bold">{healthData.cpu_percent}%</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Usage</p>
                </div>
                <Cpu className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ACS Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wifi className="w-5 h-5" />
            ACS Settings (GenieACS)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Periodic Inform Interval (detik)
              </label>
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-2">
                Interval waktu ONT menghubungi server ACS. Semakin kecil, task WiFi akan lebih cepat dieksekusi.
              </p>
              <div className="flex gap-3 items-center">
                <input
                  type="number"
                  min="10"
                  max="3600"
                  value={acsSettings.periodic_inform_interval}
                  onChange={(e) => setAcsSettings(prev => ({
                    ...prev,
                    periodic_inform_interval: parseInt(e.target.value) || 30
                  }))}
                  className="w-32 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">detik (min: 10, max: 3600)</span>
                <Button
                  onClick={updateAcsSettings}
                  disabled={acsSettings.loading}
                  size="sm"
                >
                  {acsSettings.loading ? "Menyimpan..." : "Simpan"}
                </Button>
              </div>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-200 bg-blue-50 dark:bg-slate-700 p-3 rounded-lg">
              <strong>Rekomendasi:</strong> 30 detik untuk respons cepat, 60-200 detik untuk hemat bandwidth.
              Perubahan akan berlaku setelah ONT melakukan inform berikutnya.
            </div>

          </div>
        </CardContent>
      </Card>


      {/* Maintenance Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Maintenance Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              onClick={handleOptimizeDatabase}
              disabled={loading}
              className="h-auto py-4 flex-col gap-2"
              variant="outline"
            >
              <Database className="w-6 h-6" />
              <span>Optimize Database</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Rebuild indexes</span>
            </Button>

            <Button
              onClick={handleClearCache}
              disabled={loading}
              className="h-auto py-4 flex-col gap-2"
              variant="outline"
            >
              <Trash2 className="w-6 h-6" />
              <span>Clear Cache</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Free up memory</span>
            </Button>

            <Button
              onClick={handleDeleteOldLogs}
              disabled={loading}
              className="h-auto py-4 flex-col gap-2"
              variant="outline"
            >
              <Clock className="w-6 h-6" />
              <span>Delete Old Logs</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Clean up space</span>
            </Button>

            <Button
              onClick={() => {
                fetchHealthCheck();
                fetchDbStats();
                toast.success('Data refreshed');
              }}
              className="h-auto py-4 flex-col gap-2"
              variant="outline"
            >
              <RefreshCw className="w-6 h-6" />
              <span>Refresh Stats</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Update data</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Jadwal Backup Otomatis (scheduler v2, per tenant) */}
      <BackupSchedulesCard />

      {/* Full Database Backup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Manual Full Database Backup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 dark:text-yellow-400" />
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900">Full Backup (All Tenants)</h4>
                  <p className="text-sm text-yellow-700 mt-1 dark:text-yellow-300">
                    This will backup ALL database collections including all tenants data.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleCreateFullBackup}
              disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              <Download className="w-4 h-4 mr-2" />
              Create Full Backup
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Backup List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Backup History ({backups.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {backups.length === 0 ? (
              <p className="text-center text-slate-500 py-8 dark:text-slate-400">No backups found</p>
            ) : (
              backups.map((backup) => (
                <div
                  key={backup.name}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/50"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{backup.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatDate(backup.created)} • {backup.size_mb} MB • 
                      <span className={`ml-2 px-2 py-0.5 rounded ${
                        backup.type === 'full' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {backup.type === 'full' ? '🌍 Full' : '👤 Tenant'}
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadBackup(backup)}
                      title="Download backup file"
                      data-testid={`backup-download-${backup.name}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestoreBackup(backup)}
                      title="Restore backup ini (data saat ini akan ditimpa)"
                      data-testid={`backup-restore-${backup.name}`}
                      className="text-orange-700 border-orange-300 hover:bg-orange-50 dark:text-orange-300 dark:border-orange-700"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteBackup(backup)}
                      title="Hapus file backup ini"
                      data-testid={`backup-delete-${backup.name}`}
                      className="text-red-700 border-red-300 hover:bg-red-50 dark:text-red-300 dark:border-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Database Statistics */}
      {dbStats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Database Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg dark:bg-blue-900/30">
                <p className="text-sm text-blue-600 font-medium dark:text-blue-400">Collections</p>
                <p className="text-2xl font-bold text-blue-900">{dbStats.collections}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg dark:bg-green-900/30">
                <p className="text-sm text-green-600 font-medium dark:text-green-400">Data Size</p>
                <p className="text-2xl font-bold text-green-900">{dbStats.data_size_mb} MB</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg dark:bg-purple-900/30">
                <p className="text-sm text-purple-600 font-medium dark:text-purple-400">Indexes</p>
                <p className="text-2xl font-bold text-purple-900">{dbStats.indexes}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg dark:bg-orange-900/30">
                <p className="text-sm text-orange-600 font-medium dark:text-orange-400">Index Size</p>
                <p className="text-2xl font-bold text-orange-900">{dbStats.index_size_mb} MB</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Collection Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(dbStats.collection_details).map(([name, stats]) => (
                  <div key={name} className="flex justify-between items-center p-3 bg-slate-50 rounded dark:bg-slate-900">
                    <span className="text-sm font-medium">{name}</span>
                    <div className="text-right">
                      <p className="text-sm font-bold">{stats.count.toLocaleString()}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{stats.size_mb} MB</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Old Logs Dialog */}
      {deleteLogsDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    Delete Old Logs
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Clean up old log files to free disk space
                  </p>
                </div>
              </div>
            </div>

            {/* Current Disk Info */}
            {healthData && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Current Disk Usage
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      {healthData.disk.free_gb}GB free of {healthData.disk.total_gb}GB
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {healthData.disk.percent}%
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Days Input with Slider */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Delete logs older than:
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={deleteLogsDialog.days}
                    onChange={(e) => setDeleteLogsDialog({
                      ...deleteLogsDialog,
                      days: parseInt(e.target.value) || 1
                    })}
                    className="w-20 px-3 py-2 border border-slate-300 rounded-lg text-center font-bold text-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  />
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    days
                  </span>
                </div>
              </div>

              {/* Slider */}
              <input
                type="range"
                min="1"
                max="180"
                value={deleteLogsDialog.days}
                onChange={(e) => setDeleteLogsDialog({
                  ...deleteLogsDialog,
                  days: parseInt(e.target.value)
                })}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-700 slider-orange"
              />

              {/* Preset Buttons */}
              <div className="flex gap-2 flex-wrap">
                {[7, 15, 30, 60, 90].map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setDeleteLogsDialog({
                      ...deleteLogsDialog,
                      days: preset
                    })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      deleteLogsDialog.days === preset
                        ? 'bg-orange-600 text-white shadow-lg scale-105'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                    }`}
                  >
                    {preset}d
                  </button>
                ))}
              </div>

              {/* Recommendation */}
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
                <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  💡 Recommendation:
                </p>
                <div className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                  {deleteLogsDialog.days <= 7 && (
                    <p>⚡ <strong>Aggressive:</strong> Quick cleanup, minimal history</p>
                  )}
                  {deleteLogsDialog.days > 7 && deleteLogsDialog.days <= 30 && (
                    <p>✅ <strong>Recommended:</strong> Balance between history & space</p>
                  )}
                  {deleteLogsDialog.days > 30 && deleteLogsDialog.days <= 90 && (
                    <p>🔒 <strong>Conservative:</strong> Keep longer history for audit</p>
                  )}
                  {deleteLogsDialog.days > 90 && (
                    <p>📦 <strong>Maximum retention:</strong> Requires more disk space</p>
                  )}
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0 dark:text-yellow-400" />
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  This action cannot be undone. Log files older than {deleteLogsDialog.days} days will be permanently deleted.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteLogsDialog({ open: false, days: 30 })}
                className="flex-1 px-4 py-2.5 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteLogs}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-medium hover:shadow-lg hover:scale-105 transition-all"
              >
                Delete Logs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Logs Dialog */}
      {previewLogsDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col animate-fadeIn">
            {/* Header */}
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Database className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                      Preview: Files to Delete
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Review and select files before deletion
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setPreviewLogsDialog({ open: false, files: [], selectedFiles: [], totalSize: 0, days: 30 })}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                </button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Total Files</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{previewLogsDialog.files.length}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium">Selected</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">{previewLogsDialog.selectedFiles.length}</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                  <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Total Size</p>
                  <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">{previewLogsDialog.totalSize}MB</p>
                </div>
              </div>

              {/* Select All / Deselect All */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setPreviewLogsDialog({
                    ...previewLogsDialog,
                    selectedFiles: previewLogsDialog.files.map(f => f.path)
                  })}
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors dark:bg-blue-900/40 dark:text-blue-300"
                >
                  ✓ Select All
                </button>
                <button
                  onClick={() => setPreviewLogsDialog({
                    ...previewLogsDialog,
                    selectedFiles: []
                  })}
                  className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors dark:bg-slate-800 dark:text-slate-200"
                >
                  ✗ Deselect All
                </button>
              </div>
            </div>

            {/* File List (Scrollable) */}
            <div className="flex-1 overflow-y-auto p-6 space-y-2">
              {previewLogsDialog.files.map((file) => {
                const isSelected = previewLogsDialog.selectedFiles.includes(file.path);
                
                return (
                  <div
                    key={file.path}
                    onClick={() => {
                      if (isSelected) {
                        setPreviewLogsDialog({
                          ...previewLogsDialog,
                          selectedFiles: previewLogsDialog.selectedFiles.filter(p => p !== file.path)
                        });
                      } else {
                        setPreviewLogsDialog({
                          ...previewLogsDialog,
                          selectedFiles: [...previewLogsDialog.selectedFiles, file.path]
                        });
                      }
                    }}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:text-blue-400"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 dark:text-white text-sm">
                          {file.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {file.age_days} days old • {file.size_mb} MB
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 dark:text-blue-400" />
                    )}
                  </div>
                );
              })}

              {previewLogsDialog.files.length === 0 && (
                <div className="text-center py-12">
                  <Database className="w-16 h-16 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">No log files found</p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>{previewLogsDialog.selectedFiles.length}</strong> file(s) selected
                </div>
                {previewLogsDialog.selectedFiles.length > 0 && (
                  <div className="text-sm font-medium text-orange-600 dark:text-orange-400">
                    Will free up ~{(previewLogsDialog.selectedFiles.reduce((sum, path) => {
                      const file = previewLogsDialog.files.find(f => f.path === path);
                      return sum + (file?.size_mb || 0);
                    }, 0)).toFixed(2)} MB
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setPreviewLogsDialog({ open: false, files: [], selectedFiles: [], totalSize: 0, days: 30 })}
                  className="flex-1 px-4 py-2.5 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={executeDeletion}
                  disabled={previewLogsDialog.selectedFiles.length === 0}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    previewLogsDialog.selectedFiles.length === 0
                      ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:shadow-lg hover:scale-105'
                  }`}
                >
                  <Trash2 className="w-4 h-4 inline mr-2" />
                  Delete Selected ({previewLogsDialog.selectedFiles.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Dialog */}
      {progressDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            {/* Header */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {progressDialog.title}
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                {progressDialog.message}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Progress
                </span>
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                  {progressDialog.progress}%
                </span>
              </div>
              
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    progressDialog.status === 'success'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                      : progressDialog.status === 'error'
                      ? 'bg-gradient-to-r from-red-500 to-pink-500'
                      : 'bg-gradient-to-r from-blue-500 to-indigo-500 animate-pulse'
                  }`}
                  style={{ width: `${progressDialog.progress}%` }}
                />
              </div>

              {/* Time/Speed Indicator */}
              {progressDialog.status === 'running' && progressDialog.progress < 100 && (
                <div className="flex items-center justify-center mt-3 text-xs text-slate-500 dark:text-slate-400">
                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  <span>Processing...</span>
                </div>
              )}
            </div>

            {/* Status Icon */}
            <div className="flex justify-center mb-4">
              {progressDialog.status === 'running' && (
                <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin dark:text-blue-400" />
                </div>
              )}
              {progressDialog.status === 'success' && (
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-bounce">
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
              )}
              {progressDialog.status === 'error' && (
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
              )}
            </div>

            {/* Details Log */}
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 max-h-40 overflow-y-auto">
              <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 uppercase">
                Process Log
              </h4>
              <div className="space-y-1">
                {progressDialog.details.map((detail, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400 animate-slideIn"
                  >
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span className="flex-1">{detail}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Close Button (only for success/error) */}
            {(progressDialog.status === 'success' || progressDialog.status === 'error') && (
              <button
                onClick={() => setProgressDialog(prev => ({ ...prev, open: false }))}
                className="mt-4 w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminTools;
